/* eslint-disable i18next/no-literal-string -- markup, CSP directives and escapes, not copy */
import {
  LEGACY_CONFIG_FILE,
  hostConfigBlock,
  renderWidget,
  sanitizeWidget,
  type WidgetHostConfig,
} from '@inference.engine/widget-html/web';

export interface SrcdocInput {
  document: string;
  data: Record<string, unknown>;
  /** The pinned runtime's folder: `<api>/api/v1/widget-runtimes/<hash>`. */
  runtimeUrl: string;
  assetOrigin: string;
  theme: 'light' | 'dark';
  /** Paints the theme's surface behind the widget, which is otherwise transparent over the page. */
  surface?: boolean;
  /** Pixels of the widget's own surface around it: the space a chat leaves, for a preview. */
  inset?: number;
  /** What the runtime sends back on every message, to tell it from a page the frame was sent to. */
  token: string;
  labels: WidgetHostConfig['labels'];
  /** The page's own @font-face rules; the runtime ships no fonts. */
  fontFaces: string;
  fontOrigin: string;
}

export interface Srcdoc {
  srcdoc: string;
  missing: string[];
  removed: string[];
}

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

/**
 * The frame's whole page. The document is sanitized, filled, and sanitized again here: the server
 * sanitized it when it was stored, but what reaches the page is checked by the page.
 */
/**
 * A chart's spec is JSON inside its \`<mf-chart>\`, shown as text until the runtime draws it, and the
 * runtime's own styles come over the network; a reload (a theme switch) would flash it. Inline, so
 * it holds from the first paint. The runtime's empty and error notes set their own colour and size.
 */
const HIDE_CHART_SPECS = 'mf-chart{display:block;color:transparent;font-size:0}';

export function buildSrcdoc(input: SrcdocInput): Srcdoc {
  const template = sanitizeWidget(input.document);
  const rendered = renderWidget(template.document, input.data);
  const body = sanitizeWidget(rendered.html);
  const runtimeOrigin = new URL(input.runtimeUrl).origin;
  const config: WidgetHostConfig = {
    data: input.data,
    theme: input.theme,
    labels: input.labels,
    missing: rendered.missing,
    removed: [...template.removed, ...body.removed],
    token: input.token,
  };
  const policy = [
    "default-src 'none'",
    `script-src ${runtimeOrigin}`,
    `style-src ${runtimeOrigin} 'unsafe-inline'`,
    `img-src ${input.assetOrigin} data:`,
    `font-src ${input.fontOrigin}`,
    "connect-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
  ].join('; ');
  const runtime = escapeAttribute(input.runtimeUrl);
  // Resolves beside the runtime folder: `<api>/api/v1/widget-runtimes/legacy-config.js`.
  const legacyConfig = escapeAttribute(new URL(LEGACY_CONFIG_FILE, input.runtimeUrl).href);

  const srcdoc = `<!doctype html><html class="${input.theme === 'dark' ? 'dark' : ''}"><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${escapeAttribute(policy)}"><style>${HIDE_CHART_SPECS}${input.fontFaces.replace(/<\//g, '<\\/')}${input.surface ? ':root{background:var(--color-background)}' : ''}${input.inset ? `:root body{padding:${input.inset}px}` : ''}</style><link rel="stylesheet" href="${runtime}/styles.css">${hostConfigBlock(config)}</head><body>${body.document}<script src="${legacyConfig}"></script><script src="${runtime}/host.js"></script></body></html>`;

  return { srcdoc, missing: rendered.missing, removed: config.removed };
}
